import React, { Component } from "react";

import Menu from "./menu";

import "./css/brite.css";

import briteContentHeroPNG from "./images/brite/content_hero.png";
import briteIphoneWiresPNG from "./images/brite/iphone_wires.png";
import briteWebConsolePNG from "./images/brite/web_console.png";
import briteInboxModelPNG from "./images/brite/inbox_model.png";
import briteInboxModel2xPNG from "./images/brite/inbox_model_2x.png";
import briteComposeTopicPNG from "./images/brite/compose_topic.png";
import briteComposeTopic2xPNG from "./images/brite/compose_topic_2x.png";
import briteComposePrivateMessagePNG from "./images/brite/compose_private_message.png";
import briteComposePrivateMessage2xPNG from "./images/brite/compose_private_message_2x.png";

class Swipe extends Component {
  render() {
    return (
      <div className="brite-page">
        <Menu type="projects" />
        <div className="intro section-wrapper">
          <div className="section">
            <div className="image-wrapper">
              <img src={briteContentHeroPNG} alt="Screenshot of BriteIM application" />
            </div>
          </div>
          <div className="credits">
            <p>
              Work done while employed by MFLUENT, LLC
              <br />
              Role: product design, prototyping, web development, branding
            </p>
          </div>
        </div>

        <div className="section-wrapper">
          <div className="section context">
            <div className="column">
              <h1>Tackling team communication</h1>
              <h2>Opportunity</h2>
              <p>
                Relying on email for team communication is not a great experience. Most teams introduce additional
                technologies: personal messaging, chat rooms, IRC, forums, wikis, etc. These however come at the cost of
                information fragmentation. If we examine what each of these technologies offers through the lens of an
                organization we find that there is no perfect use case that can meet these needs. A modern team
                communication tool has to optimize for the different communication patterns instead of trying to broaden
                a particular one to meet all the communication needs of the organization.
              </p>

              <h2>Introduction</h2>
              <p>
                In my five year tenure at MFLUENT we spent considerable energy trying to improve email. It seems
                appropriate then that my final project ended up being an attempt to replace email altogether as a team
                communication tool. BriteIM predates Slack — the de facto choice for team communication today — and
                while there is a lot of overlap in features and design between the two products there are also some
                philosophical differences in the approach.
              </p>
              <p>
                As part of a product management team, this was my first experience at the driving end of a project with
                multiple development teams involved. As the lead designer I was responsible for shaping the product,
                prototyping, documenting and helped keep the individual dev teams in sync and productive.
              </p>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Lead by example</h2>
              <p>
                The core goal was to promote thoughtful discourse. I believed this is as much culture as it is choice of
                tool. The first message always sets the tone of the conversation. The default UI for the compose screen
                is large and invites the author to craft their message before starting a conversation. We call these
                topics and they're essentially public at the organization level and can function as both group chat as
                well as discussion threads.
              </p>
            </div>
            <div className="image-wrapper">
              <img
                srcSet={briteComposeTopicPNG + " 1x, " + briteComposeTopic2xPNG + " 2x"}
                alt="Mockup of BriteIM topic compose screen"
              />
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Meet me at the water cooler</h2>
              <p>
                Real-time messaging is also undoubtedly an important communication channel especially for remote teams
                that don't get to interact in person. In addition to topics, we also added private messages which are
                one to one conversations. Private messages provide a familiar instant messaging experience:
              </p>
            </div>
            <div className="image-wrapper">
              <img
                srcSet={briteComposePrivateMessagePNG + " 1x, " + briteComposePrivateMessage2xPNG + " 2x"}
                alt="Mockup of BriteIM chat compose screen"
              />
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Keep what is good from email and build on it</h2>
              <p>
                Focused topic-based conversation leads to useful and thoughtful conversation. The challenge is to avoid
                the inbox management problems we know too well from email. The solution was to provide a threading model
                that provides some context to a conversation and to make it easy and cheap (mentally) to get a
                conversation out of the inbox. The threading has a side effect of making conversations discoverable
                later while disturbing as few people as possible today.
              </p>
            </div>
            <div className="image-wrapper">
              <img
                srcSet={briteInboxModelPNG + " 1x, " + briteInboxModel2xPNG + " 2x"}
                alt="Mockup of BriteIM inbox model"
              />
            </div>
            <div className="column">
              <p>
                Deleting a conversation will archive it until a new message is posted. Muting a conversation will keep
                it archived until someone explicitly mentions you. Pinning keeps a conversation above the fold in the
                inbox.
              </p>
              <p>
                This model in conjunction with a powerful search system makes it easy to disconnect and not feel
                overwhelmed when you return.
              </p>
              <p>Purposefully missing: unread counts, status and typing indicators.</p>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>On the go</h2>
              <p>
                The visual language and flow were shared by both the iOS and Android clients. The app made it easy to
                retrieve a file or find a specific message on the go via a universal search function, supported quick
                switching between teams and had thoughtful and granular notification settings.{" "}
              </p>
            </div>
            <div className="image-wrapper">
              <img src={briteIphoneWiresPNG} alt="Mockup of BriteIM iPhone wireframe" />
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>The product is the ecosystem</h2>
              <p>
                Modern services have a lot of components. I designed and built the front-end to the web portal to handle
                signups, billing, team management and auditing.
              </p>
            </div>
            <div className="image-wrapper">
              <img src={briteWebConsolePNG} alt="Screenshot of BriteIM web administration console" />
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Conclusion</h2>
              <p>
                A lot has changed since. Indeed, email is no longer the main team communication tool. Yet I feel like
                many of the original issues we tried to address remain unsolved. The pendulum swung too far towards
                synchronous communication channels like group chat. This leaves everyone feeling claustrophobic and does
                not promote thoughtful discourse. I believe whatever dethrones Slack will not be a messaging tool — at
                least by today's standards.
              </p>
            </div>
            <div className="column">
              <p>
                <a href="#top" className="to-top">
                  Back to top
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Swipe;



// WEBPACK FOOTER //
// ./src/brite.js